import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, {
  Suspense,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import {
  RunLogDocument,
  RunLogNode,
} from '../../../service/AdminSpaceLoggingService';
import { FlowService } from '../../../service/FlowService';
import {
  buildInnerStepTargetId,
  buildRootNodeTargetId,
  buildSubRunNodeTargetId,
  buildSubRunTargetId,
  formatNodeDuration,
  hasErrorAnywhere,
  RUN_LOG_EXPAND_EVENT,
  RunLogExpandEventDetail,
} from './runLogMarkdown';

interface ExpandRequest {
  pathIds: string[];
  deepestId: string;
  version: number;
}

const ExpandRequestContext = createContext<ExpandRequest | null>(null);

const RunLogNodeContent = React.lazy(() => import('./RunLogNodeContent'));

const FLOW_NAME_STALE_MS = 5 * 60 * 1000;

interface RunTotals {
  tokens: number;
  cost: number;
}

const sumNodeTotals = (node: RunLogNode): RunTotals => {
  let tokens = node.tokens ?? 0;
  let cost = node.cost ?? 0;
  if (node.innerSteps) {
    for (const inner of node.innerSteps) {
      const t = sumNodeTotals(inner);
      tokens += t.tokens;
      cost += t.cost;
    }
  }
  if (node.subRun) {
    for (const sub of node.subRun.nodes) {
      const t = sumNodeTotals(sub);
      tokens += t.tokens;
      cost += t.cost;
    }
  }
  return { tokens, cost };
};

const sumRunTotals = (doc: RunLogDocument): RunTotals => {
  let tokens = 0;
  let cost = 0;
  for (const node of doc.nodes) {
    const t = sumNodeTotals(node);
    tokens += t.tokens;
    cost += t.cost;
  }
  return { tokens, cost };
};

const extractLibraryFlowId = (node: RunLogNode): string | undefined => {
  if (node.nodeType !== 'libraryNode') return undefined;
  const settings = node.settings as
    | { flowId?: string; selectedFlow?: string }
    | null
    | undefined;
  return settings?.flowId ?? settings?.selectedFlow;
};

const useLibraryFlowName = (flowId: string | undefined): string | undefined => {
  const { data } = useQuery({
    queryKey: ['space-log-flow-name', flowId],
    enabled: !!flowId,
    staleTime: FLOW_NAME_STALE_MS,
    retry: false,
    queryFn: async () => {
      const res = await FlowService.getFlow(flowId as string);
      const name = (res?.data as { name?: string } | undefined)?.name;
      return name ?? null;
    },
  });
  return data ?? undefined;
};

interface RunMarkdownViewProps {
  doc: RunLogDocument;
}

interface NodeSummaryProps {
  node: RunLogNode;
  stepNumber: string;
}

const NodeSummary: React.FC<NodeSummaryProps> = ({ node, stepNumber }) => {
  const name = node.label || node.nodeType;
  const libraryFlowName = useLibraryFlowName(extractLibraryFlowId(node));
  const subFlowName = node.subRun?.rootFlowName ?? libraryFlowName;
  return (
    <>
      <span className='sc-step'>{stepNumber}.</span>{' '}
      <span className='sc-name'>{name}</span>
      {' '}
      <span className='sc-sep'>·</span>{' '}
      <code>{node.nodeType}</code>
      {subFlowName && (
        <>
          {' '}
          <span className='sc-flow-arrow'>→</span>{' '}
          <span className='sc-flow-call'>{subFlowName}</span>
        </>
      )}
      {' '}
      <span className='sc-sep'>·</span>{' '}
      <span className='sc-dur'>{formatNodeDuration(node.durationMs)}</span>{' '}
      <span className='sc-sep'>·</span>{' '}
      <span className='sc-tok'>{(node.tokens ?? 0).toLocaleString()} tok</span>
      {node.status === 'error' && (
        <>
          {' '}
          <span className='sc-sep'>·</span>{' '}
          <span className='sc-err'>error</span>
        </>
      )}
    </>
  );
};

interface NodeContentLoadingProps {
  node: RunLogNode;
}

const NodeContentLoading: React.FC<NodeContentLoadingProps> = ({ node }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      color: '#6b7280',
      fontSize: 12,
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
    }}
  >
    Loading {node.label || node.nodeType}…
  </Box>
);

const useExpandOnTarget = (
  targetId: string,
  elementRef: React.RefObject<HTMLElement>,
  setIsOpen: (open: boolean) => void,
): void => {
  const request = useContext(ExpandRequestContext);
  const lastAppliedVersion = useRef<number | null>(null);
  useEffect(() => {
    if (!request) return;
    if (lastAppliedVersion.current === request.version) return;
    if (!request.pathIds.includes(targetId)) return;
    lastAppliedVersion.current = request.version;
    setIsOpen(true);
    if (request.deepestId !== targetId) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        elementRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    });
  }, [request, targetId, elementRef, setIsOpen]);
};

interface NodeRowProps {
  node: RunLogNode;
  stepNumber: string;
  depth: number;
  targetId: string;
}

const NodeRow: React.FC<NodeRowProps> = ({
  node,
  stepNumber,
  depth,
  targetId,
}) => {
  const [isOpen, setIsOpen] = useState(() => hasErrorAnywhere(node));
  const [, startTransition] = useTransition();
  const elementRef = useRef<HTMLElement | null>(null);
  useExpandOnTarget(targetId, elementRef, setIsOpen);
  const statusAttr = node.status === 'error' ? 'error' : 'success';
  const handleToggle: React.MouseEventHandler<HTMLElement> = e => {
    e.preventDefault();
    startTransition(() => setIsOpen(prev => !prev));
  };
  return (
    <Box
      component='details'
      id={targetId}
      ref={elementRef}
      open={isOpen}
      data-status={statusAttr}
    >
      <Box component='summary' onClick={handleToggle}>
        <NodeSummary node={node} stepNumber={stepNumber} />
      </Box>
      {isOpen && (
        <>
          <Suspense fallback={<NodeContentLoading node={node} />}>
            <RunLogNodeContent node={node} />
          </Suspense>
          {node.innerSteps && node.innerSteps.length > 0 && (
            <>
              <p>
                <strong>Library inner steps</strong>
              </p>
              {node.innerSteps.map((inner, i) => (
                <NodeRow
                  key={`${inner.nodeId}-${i}`}
                  node={inner}
                  stepNumber={`${stepNumber}.${i + 1}`}
                  depth={depth}
                  targetId={buildInnerStepTargetId(targetId, i)}
                />
              ))}
            </>
          )}
          {node.subRun && (
            <SubflowBlock
              doc={node.subRun}
              depth={depth + 1}
              targetId={buildSubRunTargetId(targetId)}
            />
          )}
        </>
      )}
    </Box>
  );
};

interface SubflowBlockProps {
  doc: RunLogDocument;
  depth: number;
  targetId: string;
}

const SubflowBlock: React.FC<SubflowBlockProps> = ({
  doc,
  depth,
  targetId,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [, startTransition] = useTransition();
  const elementRef = useRef<HTMLElement | null>(null);
  useExpandOnTarget(targetId, elementRef, setIsOpen);
  const statusAttr = doc.status === 'error' ? 'error' : undefined;
  const handleToggle: React.MouseEventHandler<HTMLElement> = e => {
    e.preventDefault();
    startTransition(() => setIsOpen(prev => !prev));
  };
  return (
    <Box
      component='details'
      className='sc-subflow-block'
      id={targetId}
      ref={elementRef}
      open={isOpen}
      data-flow-depth={depth}
      data-status={statusAttr}
    >
      <Box
        component='summary'
        className='sc-subflow-head'
        onClick={handleToggle}
      >
        <span className='sc-subflow-badge'>FLOW</span>{' '}
        <span className='sc-subflow-arrow'>→</span>{' '}
        <span className='sc-subflow-name'>{doc.rootFlowName}</span>{' '}
        <code className='sc-subflow-id'>{doc.rootFlowId}</code>{' '}
        <span className='sc-subflow-stats'>
          {formatNodeDuration(doc.durationMs)} ·{' '}
          {doc.totalTokens.toLocaleString()} tok
        </span>
        {doc.status === 'error' && <span className='sc-err'>error</span>}
      </Box>
      {isOpen && (
        <>
          {doc.errorMessage && <blockquote>{doc.errorMessage}</blockquote>}
          {doc.nodes.map((node, i) => (
            <NodeRow
              key={`${node.nodeId}-${i}`}
              node={node}
              stepNumber={`${i + 1}`}
              depth={depth}
              targetId={buildSubRunNodeTargetId(targetId, i)}
            />
          ))}
        </>
      )}
    </Box>
  );
};

interface RootBannerProps {
  doc: RunLogDocument;
}

const RootBanner: React.FC<RootBannerProps> = ({ doc }) => {
  const processTotals = useMemo(() => sumRunTotals(doc), [doc]);
  return (
    <Box className='sc-root-banner'>
      <p className='sc-root-eyebrow'>▶ Entry agent</p>
      <Box component='h2'>{doc.rootFlowName}</Box>
      <p className='sc-root-sub'>
        <code className='sc-root-id'>{doc.rootFlowId}</code>{' '}
        <span className='sc-root-stat'>
          status <strong>{doc.status}</strong>
        </span>{' '}
        <span className='sc-root-stat'>
          {formatNodeDuration(doc.durationMs)}
        </span>{' '}
        <span className='sc-root-stat'>
          {processTotals.tokens.toLocaleString()} tok
        </span>{' '}
        <span className='sc-root-stat'>${processTotals.cost.toFixed(4)}</span>
      </p>
    </Box>
  );
};

const RunMarkdownView: React.FC<RunMarkdownViewProps> = ({ doc }) => {
  const [expandRequest, setExpandRequest] = useState<ExpandRequest | null>(
    null,
  );
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<RunLogExpandEventDetail>).detail;
      if (!detail?.pathIds?.length) return;
      setExpandRequest({
        pathIds: detail.pathIds,
        deepestId: detail.deepestId,
        version: Date.now(),
      });
    };
    window.addEventListener(RUN_LOG_EXPAND_EVENT, handler);
    return () => window.removeEventListener(RUN_LOG_EXPAND_EVENT, handler);
  }, []);
  return (
    <ExpandRequestContext.Provider value={expandRequest}>
    <Box
      sx={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        fontSize: 14,
        lineHeight: 1.55,
        color: '#1f2937',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        p: 3,
        '& > :first-of-type': { mt: 0 },
        '& > details': {
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 56px',
        },
        '& p': { m: '8px 0' },
        '& hr': {
          border: 0,
          borderTop: '1px solid #e5e7eb',
          my: 2,
        },
        '& h1, & h2, & h3': {
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: '#111827',
          m: '20px 0 10px',
        },
        '& h2': {
          fontSize: 16,
          pb: 0.5,
          borderBottom: '1px solid #e5e7eb',
        },
        '& h3': { fontSize: 14, color: '#3730a3' },
        '& code': {
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", "Fira Code", monospace',
          fontSize: '0.88em',
          background: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: 0.5,
          px: 0.5,
          py: 0.1,
          color: '#111827',
        },
        '& blockquote': {
          m: '8px 0',
          p: '6px 12px',
          background: '#fef2f2',
          borderLeft: '3px solid #dc2626',
          color: '#991b1b',
          fontSize: 13,
          borderRadius: 0.5,
        },
        '& .sc-flow-meta': {
          fontSize: 12,
          color: '#6b7280',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          m: '-4px 0 12px',
        },
        '& .sc-root-banner': {
          position: 'relative',
          mt: 0,
          mb: 3,
          px: 3,
          py: 2.25,
          borderRadius: 2,
          border: '1px solid #c7d2fe',
          borderLeft: '5px solid #4f46e5',
          background:
            'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 55%, #ffffff 100%)',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        },
        '& .sc-root-banner .sc-root-eyebrow': {
          m: 0,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: '#4338ca',
        },
        '& .sc-root-banner h2': {
          m: '4px 0 10px',
          fontSize: 26,
          lineHeight: 1.15,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: '#0f172a',
          border: 'none',
          pb: 0,
        },
        '& .sc-root-banner .sc-root-sub': {
          m: 0,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          fontSize: 12,
          color: '#475569',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
        },
        '& .sc-root-banner .sc-root-id': {
          background: 'rgba(255, 255, 255, 0.8)',
          border: '1px solid rgba(79, 70, 229, 0.2)',
          color: '#312e81',
          fontSize: 11.5,
          px: 0.75,
          py: '2px',
        },
        '& .sc-root-banner .sc-root-stat': {
          color: '#475569',
        },
        '& .sc-root-banner .sc-root-stat strong': {
          color: '#0f172a',
          fontWeight: 600,
        },
        '& details': {
          m: '4px 0',
          borderRadius: 1,
          overflow: 'hidden',
        },
        '& details details': { ml: 2.5, mt: 0.5 },
        '& summary': {
          listStyle: 'none',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '2px',
          px: 1.25,
          py: 0.75,
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderLeft: '3px solid #16a34a',
          borderRadius: 1,
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
          fontSize: 12.5,
          color: '#111827',
          transition: 'background 120ms ease, border-color 120ms ease',
          '&::-webkit-details-marker': { display: 'none' },
          '&::before': {
            content: '"▸"',
            color: '#9ca3af',
            fontSize: 10,
            width: 12,
            textAlign: 'center',
            mr: 0.5,
            transition: 'transform 120ms ease',
          },
          '&:hover': { background: '#f3f4f6' },
          '& code': {
            background: '#eef2ff',
            border: '1px solid #e0e7ff',
            color: '#3730a3',
            fontSize: 11.5,
            px: 0.75,
            py: 0.1,
          },
          '& .sc-step': { color: '#6b7280', minWidth: 22 },
          '& .sc-name': { color: '#111827', fontWeight: 500 },
          '& .sc-dur': { color: '#2563eb' },
          '& .sc-tok': { color: '#6b7280' },
          '& .sc-sep': { color: '#d1d5db', px: 0.25 },
          '& .sc-flow-arrow': { color: '#6366f1', fontWeight: 700 },
          '& .sc-flow-call': {
            color: '#4338ca',
            fontWeight: 600,
            background: '#eef2ff',
            border: '1px solid #c7d2fe',
            borderRadius: 0.5,
            px: 0.75,
            py: 0.1,
            fontSize: 11.5,
          },
          '& .sc-err': {
            color: '#dc2626',
            fontWeight: 600,
            textTransform: 'uppercase',
            fontSize: 11,
            letterSpacing: 0.5,
            ml: 0.5,
          },
        },
        '& details[open] > summary': {
          background: '#f3f4f6',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        },
        '& details[open] > summary::before': {
          content: '"▾"',
        },
        '& details[data-status="error"]:not(.sc-subflow-block) > summary': {
          borderLeftColor: '#dc2626',
          background: '#fef2f2',
          '&:hover': { background: '#fee2e2' },
        },
        '& details[data-status="error"][open]:not(.sc-subflow-block) > summary':
          {
            background: '#fee2e2',
          },
        '& details > *:not(summary)': {
          px: 1.5,
          '&:first-of-type': { mt: 1 },
        },
        '& details[open]:not(.sc-subflow-block)': {
          border: '1px solid #e5e7eb',
          borderTop: 'none',
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          paddingBottom: '6px',
        },
        '& details[data-status="error"][open]:not(.sc-subflow-block)': {
          borderColor: '#fecaca',
        },
        '& .sc-subflow-block': {
          m: '14px 0 10px',
          ml: 0,
          borderRadius: 2,
          border: '1px solid',
          borderLeftWidth: '4px',
          overflow: 'hidden',
          position: 'relative',
        },
        '& .sc-subflow-block[data-flow-depth="1"]': {
          borderColor: '#c7d2fe',
          borderLeftColor: '#6366f1',
          background:
            'linear-gradient(180deg, #eef2ff 0%, #f5f7ff 60%, #ffffff 100%)',
        },
        '& .sc-subflow-block[data-flow-depth="2"]': {
          borderColor: '#e9d5ff',
          borderLeftColor: '#a855f7',
          background:
            'linear-gradient(180deg, #faf5ff 0%, #fdfaff 60%, #ffffff 100%)',
        },
        '& .sc-subflow-block[data-flow-depth="3"]': {
          borderColor: '#99f6e4',
          borderLeftColor: '#14b8a6',
          background:
            'linear-gradient(180deg, #f0fdfa 0%, #f6fefc 60%, #ffffff 100%)',
        },
        '& .sc-subflow-block[data-flow-depth="4"]': {
          borderColor: '#fecdd3',
          borderLeftColor: '#f43f5e',
          background:
            'linear-gradient(180deg, #fff1f2 0%, #fff7f7 60%, #ffffff 100%)',
        },
        '& .sc-subflow-block > summary.sc-subflow-head': {
          listStyle: 'none',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          px: 1.75,
          py: 1.25,
          border: 'none',
          borderRadius: 0,
          background: 'transparent',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: 13,
          color: '#111827',
          transition: 'background 120ms ease',
          '&::-webkit-details-marker': { display: 'none' },
          '&::before': {
            content: '"▸"',
            color: '#6b7280',
            fontSize: 12,
            width: 12,
            textAlign: 'center',
            mr: 0.25,
          },
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.55)',
          },
          '& .sc-subflow-badge': {
            display: 'inline-block',
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: 1,
            px: 0.75,
            py: '2px',
            borderRadius: 0.5,
            color: '#fff',
            textTransform: 'uppercase',
            lineHeight: 1.4,
          },
          '& .sc-subflow-arrow': {
            color: '#6b7280',
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1,
          },
          '& .sc-subflow-name': {
            fontWeight: 700,
            color: '#0f172a',
            fontSize: 14.5,
            letterSpacing: '-0.01em',
          },
          '& .sc-subflow-id': {
            fontSize: 11,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
            background: 'rgba(255,255,255,0.75)',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            color: '#334155',
            px: 0.75,
            py: '1px',
            borderRadius: 0.5,
          },
          '& .sc-subflow-stats': {
            marginLeft: 'auto',
            color: '#6b7280',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, "Cascadia Code", monospace',
            fontSize: 11.5,
          },
          '& .sc-err': {
            color: '#dc2626',
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: 10.5,
            letterSpacing: 0.7,
            background: '#fee2e2',
            border: '1px solid #fecaca',
            px: 0.75,
            py: '1px',
            borderRadius: 0.5,
          },
        },
        '& .sc-subflow-block[open] > summary.sc-subflow-head::before': {
          content: '"▾"',
        },
        '& .sc-subflow-block[open] > summary.sc-subflow-head': {
          borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
        },
        '& .sc-subflow-block[data-flow-depth="1"] .sc-subflow-badge': {
          background: '#6366f1',
        },
        '& .sc-subflow-block[data-flow-depth="2"] .sc-subflow-badge': {
          background: '#a855f7',
        },
        '& .sc-subflow-block[data-flow-depth="3"] .sc-subflow-badge': {
          background: '#14b8a6',
        },
        '& .sc-subflow-block[data-flow-depth="4"] .sc-subflow-badge': {
          background: '#f43f5e',
        },
        '& .sc-subflow-block[data-status="error"]': {
          borderColor: '#fecaca',
          borderLeftColor: '#dc2626',
        },
        '& .sc-subflow-block[data-status="error"] .sc-subflow-badge': {
          background: '#dc2626',
        },
        '& .sc-subflow-block > *:not(summary)': {
          px: 2,
          '&:first-of-type': { mt: 1.25 },
        },
        '& .sc-subflow-block > details:last-of-type': {
          mb: 1.25,
        },
        '& strong': { fontWeight: 600, color: '#374151' },
      }}
    >
      <RootBanner doc={doc} />
      {doc.errorMessage && <blockquote>{doc.errorMessage}</blockquote>}
      {doc.nodes.map((node, i) => (
        <NodeRow
          key={`${node.nodeId}-${i}`}
          node={node}
          stepNumber={`${i + 1}`}
          depth={0}
          targetId={buildRootNodeTargetId(i)}
        />
      ))}
    </Box>
    </ExpandRequestContext.Provider>
  );
};

export default RunMarkdownView;
